import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

enum EBillingTerm {
  WEEKLY,
  BIWEEKLY,
  MONTHLY,
}
enum EPaymentType {
  POSTPAID,
  PREPAID,
}

interface IProfile {
  id: string;
  name: string;
  address: string;
  phoneNumber: string;
  website: string;
  employeeUsername: string;
  accountManagerEmail: string;
  ratingEmail: string;
  billingEmail: string;
  dailyReportEmail: string;
  supportEmail: string;
  clientInitialBalance: number;
  clientCreditLimit: number;
  flexAmount: number;
  clientAlert1: number;
  clientAlert2: number;
  clientAlert3: number;
  providerInitialBalance: number;
  providerCreditLimit: number;
  providerAlert1: number;
  providerAlert2: number;
  providerAlert3: number;
  paymentTerm: number;
  billingTerm: EBillingTerm;
  paymentType: EPaymentType;
  beneficiaryName: string;
  bankName: string;
  bankAddress: string;
  accountNumber: string;
  iban: string;
  swiftCode: string;
  bankPhoneNumber: string;
  invoiceTemplateId: number;
  countryId: number;
  currencyId: number;
  isDeleted: boolean | "";
  usesVpn?: boolean;
}

export const fetchProfiles = async (
  params: Params<IProfile>,
): Promise<IPageable<IProfile>> => {
  try {
    console.log("Fetching profiles...");
    const response = await axiosInstance.get("/profiles", {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching profiles:", error.message || error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the profiles.",
    );
  }
};

export async function fetchProfile(id: IProfile["id"]): Promise<IProfile> {
  try {
    const response = await axiosInstance.get("/profiles/" + id);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the profile.",
    );
  }
}

interface ProfilesCacheData {
  profileOptions: { label: string; value: string }[];
  profileMap: Record<
    string,
    { name: string; accountManagerEmail: string; currencyId: string; usesVpn?: boolean }
  >;
}

export const useProfilesQuery = (): UseQueryResult<
  ProfilesCacheData,
  Error
> => {
  const queryKey = ["profiles", JSON.stringify({ sort: "name", size: 10000 })];
  return useQuery({
    queryKey,
    queryFn: async () => {
      const profiles = await fetchProfiles({
        sort: "name",
        size: 10000,
        isDeleted: "",
      });
      const profileOptions = profiles.content
        .filter((profile: IProfile) => profile.isDeleted === false)
        .map((profile: IProfile) => ({
          label: profile.name,
          value: profile.id,
        }));
      const profileMap = profiles.content.reduce(
        (acc, profile: IProfile) => {
          acc[profile.id] = {
            name: profile.name,
            accountManagerEmail: profile.accountManagerEmail,
            currencyId: String(profile.currencyId || ""),
            usesVpn: profile.usesVpn ?? false,
          };
          return acc;
        },
        {} as Record<
          string,
          { name: string; accountManagerEmail: string; currencyId: string; usesVpn?: boolean }
        >,
      );
      return { profileOptions, profileMap };
    },
  });
};

export const useProfileQuery = (
  id: IProfile["id"],
): UseQueryResult<IProfile, Error> => {
  const queryKey = ["profile", id];
  return useQuery({
    queryKey,
    queryFn: () => fetchProfile(id),
    enabled: Boolean(id),
  });
};

export const useProfilesDropdown = (
  filterDeleted: boolean = false,
): UseQueryResult<{ label: string; value: string }[], Error> => {
  const params: Record<string, unknown> = {
    sort: "name",
    size: 10000,
    isDeleted: filterDeleted,
  };
  const queryKey = ["profiles-dropdown", filterDeleted];
  return useQuery<{ label: string; value: string }[], Error>({
    queryKey,
    queryFn: async () => {
      const profiles = await fetchProfiles(params);
      return profiles.content.map((profile: IProfile) => ({
        label: profile.name,
        value: profile.id,
      }));
    },
  });
};
